/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSortBy, useTable } from "react-table";
import usePointerSort from "../../hooks/usePointerSort";
import { DefaultCellForColumn } from "../shared/Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../shared/Table/DefaultHeaderForColumn";
import {
  MarketRow,
  MarketTableBody,
  MarketsTableCol,
  MarketsTableTable,
  MarketsTableWrapper,
} from "./style";
import { buildMarketsColumns, chartDimensions } from "./MarketsTable.helpers";
import {
  IMarketData,
  IMarketsTableProps,
} from "./MarketsTable.types";

export type {
  IMarketData,
  IOptionMarketData,
  IPerpetualMarketData,
} from "./MarketsTable.types";

function MarketsTable({
  type,
  marketsData,
  onRowClick,
  onWatchlist,
  highlightedIndex,
  enableReorder,
  onReorder,
  isMobile,
}: IMarketsTableProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "CommandModal.MarketsTable",
  });
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  // Pointer-events based drag — works for mouse, touch (long-press), and pen.
  const rowSort = usePointerSort({
    enabled: !!enableReorder && !!onReorder,
    onReorder: (from, to) => onReorder?.(from, to),
  });
  const dragIndex = rowSort.dragIndex;
  const overIndex = rowSort.overIndex;

  const columns = useMemo(
    () => buildMarketsColumns(type, t, isMobile, onWatchlist),
    [isMobile, onWatchlist, t, type]
  );

  const assignRef = useCallback(
    (ref: HTMLTableRowElement | null, index: number) => {
      // eslint-disable-next-line no-param-reassign
      rowRefs.current[index] = ref;
    },
    [rowRefs]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: marketsData,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        },
        autoResetSortBy: false,
      } as any,
      useSortBy
    );

  // Scroll to selected row
  useEffect(() => {
    if (highlightedIndex !== undefined) {
      const ref = rowRefs.current[highlightedIndex];
      if (ref) {
        ref.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [highlightedIndex]);

  return (
    <MarketsTableWrapper>
      <MarketsTableTable {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key, ...trProps } = headerGroup.getHeaderGroupProps();
            return (
              <tr key={key} {...trProps}>
                {headerGroup.headers.map((column: any) => {
                  const { key, ...thProps } = column.getHeaderProps(column.getSortByToggleProps());
                  return (
                    // <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    <th key={key} {...thProps}>
                      {column.render("Header")}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <MarketTableBody {...getTableBodyProps()}>
          {rows.map((row, index) => {
            prepareRow(row);
            const { key, ...rowProps } = row.getRowProps();
            const dragProps = enableReorder
              ? {
                  ...rowSort.getItemProps(index),
                  title: "Drag to reorder",
                }
              : {};
            return (
              <MarketRow
                key={key}
                {...rowProps}
                {...dragProps}
                ref={(ref) => assignRef(ref, index)}
                isSelected={
                  isMobile
                    ? false
                    : (row.original as IMarketData).isCurrentMarket ||
                      index === highlightedIndex
                }
                dragging={dragIndex === index}
                dragOver={overIndex === index && dragIndex !== index}
                onClick={() => onRowClick(row.original as any)}
                delay={0.04 * index}
              >
                {row.cells.map((cell) => {
                  const { key, ...marketsTableColProps } = cell.getCellProps();
                  return (
                    <MarketsTableCol key={key} {...marketsTableColProps}
                      style={
                        cell.column.id === "indexHistory"
                          ? {
                              width: chartDimensions.width,
                            }
                          : undefined
                      }
                    >
                      {cell.render("Cell")}
                    </MarketsTableCol>
                  );
                })}
              </MarketRow>
            );
          })}
        </MarketTableBody>
      </MarketsTableTable>
    </MarketsTableWrapper>
  );
}

export default MarketsTable;
